import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Settings, RefreshCw, Check, Globe, Loader2, AlertTriangle, Shield, X, Wifi, Lock, Eye, EyeOff, Play } from "lucide-react";

export const SettingsManagement = () => {
  const [revealQuotas, setRevealQuotas] = useState({
    global_daily_limit: 50,
    per_provider_daily_limit: 5,
    reveal_cooldown_seconds: 5
  });
  const [editQuotas, setEditQuotas] = useState({
    global_daily_limit: 50,
    per_provider_daily_limit: 5,
    reveal_cooldown_seconds: 5
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { token } = useAuth();
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Proxy configuration state
  const [proxyConfig, setProxyConfig] = useState({
    enabled: false,
    username: "",
    password: "",
    default_server: "new-york"
  });
  const [proxyServers, setProxyServers] = useState([]);
  const [savingProxy, setSavingProxy] = useState(false);
  const [testingProxy, setTestingProxy] = useState(false);
  const [proxyTestResult, setProxyTestResult] = useState(null);

  // Security status state
  const [securityStatus, setSecurityStatus] = useState(null);
  const [loadingSecurity, setLoadingSecurity] = useState(false);

  // Bot status state
  const [botStatus, setBotStatus] = useState(null);
  const [loadingBot, setLoadingBot] = useState(false);
  const [restartingBot, setRestartingBot] = useState(false);

  // Change admin password
  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Les nouveaux mots de passe ne correspondent pas");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères");
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Mot de passe modifié avec succès");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du changement de mot de passe");
    } finally {
      setChangingPassword(false);
    }
  };

  const fetchSettings = useCallback(async () => {
    try {
      const [quotasRes, proxyConfigRes, proxyServersRes, securityRes] = await Promise.all([
        axios.get(`${API}/admin/settings/reveal-quotas`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/proxy/config`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/proxy/servers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/settings/security-status`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);
      setRevealQuotas(quotasRes.data);
      setEditQuotas({
        global_daily_limit: quotasRes.data.global_daily_limit,
        per_provider_daily_limit: quotasRes.data.per_provider_daily_limit,
        reveal_cooldown_seconds: quotasRes.data.reveal_cooldown_seconds
      });
      setProxyConfig(proxyConfigRes.data);
      setProxyServers(proxyServersRes.data);
      if (securityRes.data) setSecurityStatus(securityRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des paramètres");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
    fetchBotStatus();
  }, [fetchSettings]);

  const saveRevealQuotas = async () => {
    if (editQuotas.global_daily_limit < 1 || editQuotas.global_daily_limit > 500) {
      toast.error("La limite globale doit être entre 1 et 500");
      return;
    }
    if (editQuotas.per_provider_daily_limit < 1 || editQuotas.per_provider_daily_limit > 100) {
      toast.error("La limite par fournisseur doit être entre 1 et 100");
      return;
    }
    if (editQuotas.reveal_cooldown_seconds < 0 || editQuotas.reveal_cooldown_seconds > 60) {
      toast.error("Le cooldown doit être entre 0 et 60");
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/reveal-quotas`, editQuotas, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRevealQuotas({...revealQuotas, ...editQuotas});
      toast.success("Quotas de révélation mis à jour");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const saveProxyConfig = async () => {
    setSavingProxy(true);
    try {
      await axios.put(`${API}/admin/proxy/config`, proxyConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Configuration proxy mise à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du proxy");
    } finally {
      setSavingProxy(false);
    }
  };

  const testProxyConnection = async () => {
    setTestingProxy(true);
    setProxyTestResult(null);
    try {
      const response = await axios.post(
        `${API}/admin/proxy/test?server_id=${proxyConfig.default_server}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProxyTestResult(response.data);
      if (response.data.success) {
        toast.success(`Proxy OK - IP: ${response.data.ip} (${response.data.country})`);
      } else {
        toast.error(`Erreur proxy: ${response.data.error}`);
      }
    } catch (error) {
      toast.error("Erreur lors du test du proxy");
      setProxyTestResult({ success: false, error: error.message });
    } finally {
      setTestingProxy(false);
    }
  };

  const handleUnblockIp = async (ip) => {
    try {
      await axios.post(`${API}/admin/settings/unblock-ip?ip=${encodeURIComponent(ip)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`IP ${ip} débloquée`);
      // Refresh security status
      const res = await axios.get(`${API}/admin/settings/security-status`, { headers: { Authorization: `Bearer ${token}` } });
      setSecurityStatus(res.data);
    } catch (error) {
      toast.error("Erreur lors du déblocage");
    }
  };

  const refreshSecurityStatus = async () => {
    setLoadingSecurity(true);
    try {
      const res = await axios.get(`${API}/admin/settings/security-status`, { headers: { Authorization: `Bearer ${token}` } });
      setSecurityStatus(res.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoadingSecurity(false);
    }
  };

  const fetchBotStatus = async () => {
    setLoadingBot(true);
    try {
      const res = await axios.get(`${API}/admin/bot/status`, { headers: { Authorization: `Bearer ${token}` } });
      setBotStatus(res.data);
    } catch (error) {
      setBotStatus({ status: "unknown", uptime: "", details: "Erreur de connexion" });
    } finally {
      setLoadingBot(false);
    }
  };

  const restartBot = async () => {
    setRestartingBot(true);
    try {
      const res = await axios.post(`${API}/admin/bot/restart`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        toast.success(res.data.message);
        setTimeout(fetchBotStatus, 2000);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error("Erreur lors du redemarrage du bot");
    } finally {
      setRestartingBot(false);
    }
  };

  return (
    <div className="p-6 animate-fade-in" data-testid="settings-management">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Paramètres</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Configuration globale de l'application
        </p>
      </div>

      {loading ? (
        <Card className="bg-card border-border animate-pulse">
          <CardContent className="p-6">
            <div className="h-6 bg-muted rounded w-1/3 mb-4" />
            <div className="h-10 bg-muted rounded w-1/2" />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Reveal Quotas Card */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-primary" />
                Quotas de révélation des hosts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Configurez les limites de révélation des hosts pour l'outil "Trouver votre host".
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-3 bg-muted/30 rounded-lg text-center">
                  <div className="text-2xl font-bold text-foreground">{revealQuotas.global_daily_limit}</div>
                  <div className="text-xs text-muted-foreground">Limite globale/jour</div>
                </div>
                <div className="p-3 bg-muted/30 rounded-lg text-center">
                  <div className="text-2xl font-bold text-foreground">{revealQuotas.per_provider_daily_limit}</div>
                  <div className="text-xs text-muted-foreground">Par fournisseur/jour</div>
                </div>
                <div className="p-3 bg-muted/30 rounded-lg text-center">
                  <div className="text-2xl font-bold text-foreground">{revealQuotas.reveal_cooldown_seconds}s</div>
                  <div className="text-xs text-muted-foreground">Cooldown entre révélations</div>
                </div>
              </div>
              
              <div className="space-y-4 pt-4 border-t border-border">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="global-limit">Limite globale/jour (1-500)</Label>
                    <Input
                      id="global-limit"
                      type="number"
                      min={1}
                      max={500}
                      value={editQuotas.global_daily_limit}
                      onChange={(e) => setEditQuotas({...editQuotas, global_daily_limit: parseInt(e.target.value) || 1})}
                      data-testid="global-limit-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="provider-limit">Par fournisseur/jour (1-100)</Label>
                    <Input
                      id="provider-limit"
                      type="number"
                      min={1}
                      max={100}
                      value={editQuotas.per_provider_daily_limit}
                      onChange={(e) => setEditQuotas({...editQuotas, per_provider_daily_limit: parseInt(e.target.value) || 1})}
                      data-testid="provider-limit-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cooldown">Cooldown (0-60 sec)</Label>
                    <Input
                      id="cooldown"
                      type="number"
                      min={0}
                      max={60}
                      value={editQuotas.reveal_cooldown_seconds}
                      onChange={(e) => setEditQuotas({...editQuotas, reveal_cooldown_seconds: parseInt(e.target.value) || 0})}
                      data-testid="cooldown-input"
                    />
                  </div>
                </div>
                <Button 
                  onClick={saveRevealQuotas}
                  disabled={saving || (
                    editQuotas.global_daily_limit === revealQuotas.global_daily_limit &&
                    editQuotas.per_provider_daily_limit === revealQuotas.per_provider_daily_limit &&
                    editQuotas.reveal_cooldown_seconds === revealQuotas.reveal_cooldown_seconds
                  )}
                  data-testid="save-quotas-btn"
                >
                  {saving ? "Enregistrement..." : "Enregistrer les quotas"}
                </Button>
              </div>
              
              <div className="bg-muted/30 p-4 rounded-sm mt-4">
                <p className="text-xs text-muted-foreground">
                  <strong className="text-foreground">Exemple :</strong> Avec une limite globale de {editQuotas.global_daily_limit} et 
                  une limite par fournisseur de {editQuotas.per_provider_daily_limit}, un utilisateur peut révéler 
                  au maximum {editQuotas.per_provider_daily_limit} hosts par fournisseur et {editQuotas.global_daily_limit} hosts 
                  au total sur tous les fournisseurs par jour. Il doit attendre {editQuotas.reveal_cooldown_seconds}s entre chaque révélation.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">Note importante</p>
                  <p>
                    La modification de la limite prend effet immédiatement pour toutes les nouvelles révélations.
                    Les compteurs quotidiens se réinitialisent automatiquement à minuit (UTC).
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Admin Password Change Card */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-primary" />
                Changer le mot de passe administrateur
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Modifiez le mot de passe du compte administrateur pour sécuriser l'accès.
              </p>
              
              <div className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="current-password">Mot de passe actuel</Label>
                  <div className="relative">
                    <Input
                      id="current-password"
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Entrez le mot de passe actuel"
                      className="pr-10"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="new-password">Nouveau mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Minimum 6 caractères"
                      className="pr-10"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirmer le nouveau mot de passe</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Retapez le nouveau mot de passe"
                    data-testid="confirm-password-input"
                  />
                </div>
                
                <Button 
                  onClick={handleChangePassword}
                  disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                  className="w-full"
                  data-testid="change-password-button"
                >
                  {changingPassword ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Modification en cours...
                    </>
                  ) : (
                    <>
                      <Lock className="h-4 w-4 mr-2" />
                      Changer le mot de passe
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* NordVPN Proxy Configuration Card */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wifi className="h-5 w-5 text-primary" />
                  Bot Telegram
                </div>
                <Button variant="ghost" size="sm" onClick={fetchBotStatus} disabled={loadingBot} data-testid="refresh-bot-btn">
                  <RefreshCw className={`h-4 w-4 ${loadingBot ? 'animate-spin' : ''}`} />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Statut et gestion du bot Telegram d'analyse de domaines.
              </p>

              {botStatus && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${botStatus.status === 'running' ? 'bg-green-500 animate-pulse' : botStatus.status === 'stopped' ? 'bg-red-500' : 'bg-yellow-500'}`} />
                      <div>
                        <div className="font-medium text-sm">
                          {botStatus.status === 'running' ? 'En ligne' : botStatus.status === 'stopped' ? 'Arrete' : 'Inconnu'}
                        </div>
                        {botStatus.uptime && (
                          <div className="text-xs text-muted-foreground">Uptime: {botStatus.uptime}</div>
                        )}
                      </div>
                    </div>
                    <Button 
                      onClick={restartBot} 
                      disabled={restartingBot}
                      variant={botStatus.status === 'running' ? 'outline' : 'default'}
                      data-testid="restart-bot-btn"
                    >
                      {restartingBot ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Relance...
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4 mr-2" />
                          Relancer le bot
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              <div className="bg-muted/30 p-4 rounded-sm">
                <p className="text-xs text-muted-foreground">
                  <strong className="text-foreground">Si le bot ne repond pas :</strong> Cliquez sur "Relancer le bot" pour le redemarrer. Le bot sera operationnel en quelques secondes.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* NordVPN Proxy Configuration Card */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Configuration Proxy NordVPN
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Configurez le proxy NordVPN pour contourner les blocages IP des fournisseurs.
                Le proxy sera utilisé automatiquement lors de l'analyse des fournisseurs bloqués.
              </p>

              {/* Enable/Disable Switch */}
              <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div>
                  <Label className="text-base font-medium">Activer le proxy</Label>
                  <p className="text-xs text-muted-foreground">
                    Utiliser NordVPN pour les fournisseurs bloqués
                  </p>
                </div>
                <Switch
                  checked={proxyConfig.enabled}
                  onCheckedChange={(checked) => setProxyConfig({...proxyConfig, enabled: checked})}
                />
              </div>

              {/* Credentials */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="proxy-username">Username NordVPN</Label>
                  <Input
                    id="proxy-username"
                    type="text"
                    placeholder="Service credentials username"
                    value={proxyConfig.username}
                    onChange={(e) => setProxyConfig({...proxyConfig, username: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="proxy-password">Password NordVPN</Label>
                  <Input
                    id="proxy-password"
                    type="password"
                    placeholder="Service credentials password"
                    value={proxyConfig.password}
                    onChange={(e) => setProxyConfig({...proxyConfig, password: e.target.value})}
                  />
                </div>
              </div>

              {/* Server Selection */}
              <div className="space-y-2">
                <Label>Serveur par défaut</Label>
                <Select
                  value={proxyConfig.default_server}
                  onValueChange={(value) => setProxyConfig({...proxyConfig, default_server: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un serveur" />
                  </SelectTrigger>
                  <SelectContent>
                    {proxyServers.map((server) => (
                      <SelectItem key={server.id} value={server.id}>
                        {server.flag} {server.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Test Result */}
              {proxyTestResult && (
                <div className={`p-3 rounded-lg ${proxyTestResult.success ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                  {proxyTestResult.success ? (
                    <div className="flex items-center gap-2 text-green-400">
                      <Check className="h-4 w-4" />
                      <span className="text-sm">
                        Connecté via {proxyTestResult.server_name} - IP: {proxyTestResult.ip} ({proxyTestResult.city}, {proxyTestResult.country})
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-red-400">
                      <X className="h-4 w-4" />
                      <span className="text-sm">Erreur: {proxyTestResult.error}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center gap-3 pt-4 border-t border-border">
                <Button onClick={saveProxyConfig} disabled={savingProxy}>
                  {savingProxy ? "Enregistrement..." : "Enregistrer"}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={testProxyConnection} 
                  disabled={testingProxy || !proxyConfig.username || !proxyConfig.password}
                >
                  {testingProxy ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Test en cours...
                    </>
                  ) : (
                    <>
                      <Wifi className="h-4 w-4 mr-2" />
                      Tester la connexion
                    </>
                  )}
                </Button>
              </div>

              {/* Help text */}
              <div className="bg-muted/30 p-4 rounded-sm">
                <p className="text-xs text-muted-foreground">
                  <strong className="text-foreground">Où trouver les identifiants ?</strong><br />
                  Connectez-vous à <a href="https://my.nordaccount.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">my.nordaccount.com</a> → 
                  Services → NordVPN → Service credentials (manual setup)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Security Status Card */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  Sécurité API
                </div>
                <Button variant="ghost" size="sm" onClick={refreshSecurityStatus} disabled={loadingSecurity} data-testid="refresh-security-btn">
                  <RefreshCw className={`h-4 w-4 ${loadingSecurity ? 'animate-spin' : ''}`} />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Surveillance en temps réel des tentatives de connexion et des blocages IP.
              </p>

              {securityStatus && (
                <div className="space-y-3">
                  {/* Stats summary */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 bg-muted/30 rounded-lg text-center">
                      <div className="text-2xl font-bold text-foreground">{securityStatus.blocked_count}</div>
                      <div className="text-xs text-muted-foreground">IPs bloquées</div>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg text-center">
                      <div className="text-2xl font-bold text-foreground">{securityStatus.suspicious_count}</div>
                      <div className="text-xs text-muted-foreground">IPs suspectes</div>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg text-center">
                      <div className="text-2xl font-bold text-foreground">{securityStatus.rate_limit_tracked_ips}</div>
                      <div className="text-xs text-muted-foreground">IPs suivies</div>
                    </div>
                  </div>

                  {/* Blocked IPs */}
                  {securityStatus.blocked_ips && securityStatus.blocked_ips.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-red-400">IPs bloquées</Label>
                      {securityStatus.blocked_ips.map((item, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
                          <div>
                            <span className="font-mono text-sm text-red-400">{item.ip}</span>
                            <span className="text-xs text-muted-foreground ml-2">({item.remaining_seconds}s restantes)</span>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => handleUnblockIp(item.ip)} data-testid={`unblock-ip-${i}`}>
                            <X className="h-3 w-3 mr-1" />
                            Débloquer
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Suspicious IPs */}
                  {securityStatus.suspicious_ips && securityStatus.suspicious_ips.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-yellow-400">IPs suspectes</Label>
                      {securityStatus.suspicious_ips.map((item, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                          <span className="font-mono text-sm text-yellow-400">{item.ip}</span>
                          <span className="text-xs text-muted-foreground">{item.failed_attempts}/{item.threshold} tentatives</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {securityStatus.blocked_count === 0 && securityStatus.suspicious_count === 0 && (
                    <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                      <Check className="h-4 w-4 text-green-400" />
                      <span className="text-sm text-green-400">Aucune menace détectée</span>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-muted/30 p-4 rounded-sm">
                <p className="text-xs text-muted-foreground">
                  <strong className="text-foreground">Protections actives :</strong><br />
                  Rate limiting (120 req/min) - Brute force (5 tentatives max) - Blocage IP automatique (15 min) - Headers sécurité (HSTS, CSP, X-Frame)
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
